/**
 * Shell store — widget open/close/feedback lifecycle and the active call id.
 * Ported from the alpha WidgetStateController; emits `ringg:widget_status`
 * host events on visibility transitions.
 */
import { createStore } from "./store.js";
export function createShellStore(eventBus, initialCallMode = "audio", 
// Permanently-expanded (embedded) widget: starts open, and minimize/close
// keep the panel on screen — there is no trigger to reopen it from.
defaultExpanded = false) {
    // Two independent flags, exactly like the production useWidgetState hook:
    // minimizing must NOT forget a pending feedback screen — reopening shows it
    // again until it is submitted or skipped (which go through close()).
    let isOpen = defaultExpanded;
    let feedbackPending = false;
    let currentCallId = null;
    let callMode = initialCallMode;
    const buildViewState = () => {
        if (!isOpen)
            return "closed";
        return feedbackPending ? "feedback" : "open";
    };
    const build = () => ({ viewState: buildViewState(), currentCallId, callMode });
    const handle = createStore(build);
    const store = {
        getSnapshot: handle.getSnapshot,
        subscribe: handle.subscribe,
        dispose: handle.dispose,
        get isOpen() {
            return isOpen;
        },
        get isShowingFeedback() {
            return feedbackPending;
        },
        open() {
            // No-op while already visible. A pending feedback flag survives here, so
            // the controller's endCall sequence (showFeedback() then open()) and a
            // minimize → reopen both land on the feedback view.
            if (isOpen)
                return;
            isOpen = true;
            eventBus.emit("ringg:widget_status", { status: "maximised", mode: callMode });
            handle.commit();
        },
        minimize() {
            // A permanently-expanded widget has no trigger to reopen from — hiding
            // the panel would strand the user, so minimize is a full no-op (no state
            // change, no "minimised" event), matching the production hook.
            if (defaultExpanded)
                return;
            if (!isOpen)
                return;
            // Deliberately keeps feedbackPending: reopening restores the feedback view.
            isOpen = false;
            eventBus.emit("ringg:widget_status", { status: "minimised", mode: callMode });
            handle.commit();
        },
        close() {
            feedbackPending = false;
            currentCallId = null;
            // defaultExpanded: the panel stays visible (and no "minimised" event) —
            // only the feedback/call state resets, as in the production hook where
            // close() delegates to a no-op minimize().
            if (!defaultExpanded) {
                isOpen = false;
                eventBus.emit("ringg:widget_status", { status: "minimised", mode: callMode });
            }
            handle.commit();
        },
        toggle() {
            if (isOpen) {
                store.minimize();
            }
            else {
                store.open();
            }
        },
        showFeedback() {
            feedbackPending = true;
            handle.commit();
        },
        setCallMode(mode) {
            if (callMode === mode)
                return;
            callMode = mode;
            handle.commit();
        },
        setCurrentCallId(callId) {
            currentCallId = callId;
            handle.commit();
        },
    };
    return store;
}
